import intervalToDuration from 'date-fns/intervalToDuration';
import formatDuration from 'date-fns/formatDuration';
import Router from 'next/router';
import { map } from 'rxjs';

import { getApiUrl, API_URL } from '@/utils/api';
import { getTokenPayload } from '@/utils/jwt';
import { isSSR } from '@/utils/millesimaUtils';
import { ActAsCustomer, AuthUser, UserModel } from '@/models';
import { LocalStorageService } from '@/services/LocalStorage.service';
import { UntypedObject } from '@/types';

import ApiBase from './ApiBase';
import { KEYS } from '@/constants/keys';
import { PAGES } from '@/constants/pages';
import { ROLES } from '@/constants/account';

const TOKEN_MAX_BROWSER_EXPIRATION = Math.pow(2, 31) - 1;

export default class AuthService extends ApiBase {
  static baseUrl = getApiUrl(API_URL.USER, '/auth');

  log = (..._args: any[]) => undefined;

  expirationTimeout?: any;
  debug = false;
  actAsCustomer?: ActAsCustomer;
  authUser?: AuthUser;
  authToken?: string;

  constructor(ajaxService) {
    super(ajaxService);

    if (this.api.debugId) {
      this.log = this.debugLog;
    }

    this.api.authToken$.subscribe((token) => this.watchAuthToken(token));
    this.api.actAsCustomer$.subscribe((customer) => this.watchActAsCustomer(customer));
  }

  // PUBLIC METHODS

  /**
   * Connect a user via email/password
   * Supply the guest token for guest cart copy
   * @param {string} storeId The current storeId
   * @param {string} email The user email
   * @param {string} password The user password
   * @param {boolean} isPrivate True if the website is private
   * @return {Observable<string>} The connected user (JWT token payload)
   */
  connect(storeId: string, email: string, password: string, isPrivate?: boolean) {
    const payload = {
      email,
      password,
      token: this.authToken
    };
    return this.api.postJSON(`${AuthService.baseUrl}/${storeId}/signin${isPrivate ? '?isPrivate=true' : ''}`, payload).pipe(
      map((res) => this.decodeTokenFromBody(res))
    );
  }

  /**
   * Connect a user via social login
   * @param {string} storeId The current storeId
   * @param {object} socialNetworkInformation Social network information
   * @return {Observable<string>} The connected user (JWT token payload)
   */
  connectSocialNetwork(storeId: string, socialNetworkInformation: object) {
    const payload = {
      ...socialNetworkInformation
    };
    return this.api.postJSON(`${AuthService.baseUrl}/${storeId}/signinWithSocialNetwork`, payload).pipe(
      map((res) => this.decodeTokenFromBody(res))
    );
  }

  /**
   * Create a new user
   * @param {string} storeId The current storeId
   * @param {object} params The user creation parameters (email, password, birthdate, ...)
   * @return {Observable<string>} The connected user (JWT token payload)
   */
  create(storeId: string, params: Partial<UserModel>) {
    const payload = {
      ...params,
      token: this.authToken // Supply the guest token for guest cart copy
    };
    return this.api.postJSON(`${AuthService.baseUrl}/${storeId}/signup`, payload).pipe(
      map((res) => this.decodeTokenFromBody(res))
    );
  }

  /**
   * Connect a user to a new domain via token/actAs
   * @param {string} storeId the current storeId
   * @param {object} payload the auth token
   * @return {Observable} the result
   */
  connectFromOtherDomain(storeId: string, payload: any) {
    return this.api.postJSON(`${AuthService.baseUrl}/${storeId}/signin/from-other-domain`, payload).pipe(
      map((res) => this.decodeTokenFromBody(res))
    );
  }

  /**
   * Reset a user password: request backend to send a "reset password" mail to the given user.
   * The email will contain a link to the front reset password page with a specific token that
   * will be given to the changePassword method.
   * @param {string} storeId The current storeId
   * @param {string} email The user email address
   * @return {Observable} The action result
   */
  resetPassword(storeId: string, email: string) {
    return this.api.getJSON(`${AuthService.baseUrl}/${storeId}/resetPassword?email=${encodeURIComponent(email)}`);
  }

  /**
   * Change a user password after a reset request
   * @param {*} storeId The current storeId
   * @param {*} password The new user password
   * @param {*} token The token embedded in the reset passwword email
   * @return {Observable} The action result
   */
  changePassword(storeId: string, password: string, token: string) {
    return this.api.postJSON(`${AuthService.baseUrl}/${storeId}/resetPassword`, { password, token });
  }

  debugLog(...args: any[]): void {
    console.log(`MEP: AUTH[${this.api.debugId}]`, ...args);
  }

  /**
   * Disconnect a user session
   * @param {boolean} isPrivate Ture if on a private site
   */
  disconnect(isPrivate?: boolean): void {
    this.log('disconnect (USER)', this.authUser?.userId);

    console.error('Disconnect user: AUTH SERVICE 143');
    // Reset internal authentication core
    this.api.setAuthTokenCookie(undefined);
    this.setActAsCustomer(undefined);
    this.storeAuthUser(undefined);

    if (!isSSR()) {
      // Delete user info from local storage
      LocalStorageService.remove(KEYS.HASH_USER);

      // Move to privateWebsite login or home
      Router.push(isPrivate ? PAGES.PRIVATE_WEBSITE : PAGES.HOME);
    }
  }

  /**
   * Return the currently connected user
   * @return {object} The currently connected user (JWT payload) or undefined
   */
  getConnectedUser(): AuthUser | undefined {
    this.log('getConnectedUser (USER)', this.authUser?.userId);

    return (this.authUser && !this.authUser.isGuest) ? this.authUser : undefined;
  }

  // JSON WEB TOKEN

  /**
   * Get authToken cookie
   * @return {string} authToken
   */
  getAuthToken(): string {
    return this.api.authToken;
  }

  setActAsCustomer(customer) {
    this.api.setActAsCustomerCookie(customer);
  }

  watchAuthToken(token: string): void {
    this.log('TOKEN CHANGE', token?.slice(0, 10));
    this.decodeToken(token);
  }

  watchActAsCustomer(customer) {
    this.actAsCustomer = customer;
    this.log('ACTAS CHANGE', this.actAsCustomer?._id);
  }

  /**
   * Decode token received from auth request
   * @param {object} param0 The auth request response body, must contain an accessToken attribute.
   * @return {object} The connected user if any
   */
  decodeTokenFromBody({ accessToken }) {
    this.api.storeTokenFromBody(accessToken);
    this.decodeToken(accessToken);
    return this.getConnectedUser();
  }

  /**
   * Decode a JWT token payload and either store payload orr route to login page.
   * @param {string} token The JWT token
   */
  decodeToken(token: string) {
    this.log('decodeToken', !!token);
    let payload: AuthUser;

    if (token) {
      // Decode the payload part and control expiration date
      payload = getTokenPayload(token);
      if (!payload) {
        this.log('decodeToken EXPIRED (TOKEN)');
        this.routeToAuth();
      } else {
        this.log('decodeToken OK (TOKEN)', payload.userId);
      }
    }

    this.storeAuthUser(token, payload);
  }

  // INTERNAL AUTH CORE

  /**
   * Route to login page (client-side only) while preserving the actual location.
   * For server-side it is handled by the getServerSideProps function
   */
  routeToAuth(): void {
    if (!isSSR()) {
      const originalRoute = Router.asPath || PAGES.HOME;
      this.log('routeToAuth FRONT', originalRoute);
      console.error('route to auth  AUTH SERVICE : 234');
      this.api.setAuthTokenCookie(undefined);
      this.setActAsCustomer(undefined);
      this.storeAuthUser(undefined);

      Router.push({
        pathname: PAGES.AUTH,
        query: {
          origin: originalRoute
        }
      });
    }
  }

  /**
   * Store authentication information after either a user connection or the service initialization
   * @param {string} token The JWT token
   * @param {string} payload The JWT token decoded payload
   */
  storeAuthUser(token: string, payload?: AuthUser) {
    this.unwatchExpiration();

    this.authUser = payload;
    this.authToken = token;

    this.log('authStore', this.authUser?.userId);

    if (this.authUser) {
      this.authUser.isAdmin = this.hasRole(this.authUser, ROLES.ADMIN);

      if (!isSSR()) {
        this.authWatchExpiration(payload);
      }
    }
  }

  /**
   * Stop/start token expiration timer
   * @param {*} payload The JWT payload
   */
  authWatchExpiration(payload: UntypedObject) {
    this.unwatchExpiration();

    if (typeof payload?.exp !== 'undefined') {
      const now = Math.floor((new Date()).getTime() / 1000);
      const expirationTime = Math.floor(payload.exp - now);

      if (this.debug) {
        const duration = intervalToDuration({ start: 0, end: expirationTime * 1000 });
        console.debug('auth expiration timer set to', formatDuration(duration));
      }

      if (expirationTime > 0) {
        const delay = Math.min(expirationTime * 1000, TOKEN_MAX_BROWSER_EXPIRATION);
        this.log('authWatchExpiration ARM (SEC)', expirationTime);

        this.expirationTimeout = setTimeout(() => {
          this.log('authWatchExpiration DISCONNECT');
          console.error('Auth watch expiration : AUTH SERVICE 293');
          this.api.setAuthTokenCookie(undefined);
          this.unwatchExpiration();
        }, delay);
      }
    }
  }

  /**
   * Stop token expiration timer
   */
  private unwatchExpiration(): void {
    if (this.expirationTimeout) {
      this.log('authWatchExpiration CLEAR');
      clearTimeout(this.expirationTimeout);
      this.expirationTimeout = undefined;
    }
  }

  // PERMISSIONS

  /**
   * Function called to know if user has access depending on the desired role
   * @param {AuthUser} user The user that need to be tested
   * @param {string} role The role needed for the authorization
   * @return {boolean} Indication if user is authorized or not
   */
  isUserAuthorized(user: AuthUser, role: string): boolean {
    return this.hasRole(user, role);
  }

  /** Return whether the user has role or not
   * @param {AuthUser} user The user from payload in decode from token
   * @param {string} role The role to check
   * @return {boolean} Indication the user has the role or not
  */
  hasRole(user: AuthUser, role: string): boolean {
    let allowed = false;
    if (user && !user.isGuest) {
      if (!role || (user.roles?.length > 0 && user.roles.includes(role))) {
        allowed = true;
      }
    }
    this.log('hasRole', user?.userId, role, allowed);
    return allowed;
  }
}
